import { ledger } from './browser.js';

/**
 * ASK THE BROWSER TO KEEP OUR STORAGE (0.1.395).
 *
 * Two IndexedDB stores matter here, and they are not equally replaceable: `mononoke-cache` is a
 * read cache (losing it costs a re-fetch), but `mononoke-ops` is the PENDING WRITE QUEUE — work
 * done offline that has not reached the server yet. Evicting that is data loss, not a cold cache.
 *
 * ⚠️ iOS EVICTS SCRIPT-WRITABLE STORAGE. Safari clears it after roughly seven days without use,
 * and clears it earlier under storage pressure. `navigator.storage.persist()` is the documented
 * way to ask not to be in that set; on iOS an installed (home-screen) PWA is the case most likely
 * to be granted, which is exactly how this atelier runs the app.
 *
 * Best-effort by construction: the API is absent on older engines, and a browser is free to say
 * no. What is NOT allowed is not knowing — the outcome is recorded either way (a note, not an
 * error: a refusal is an answer, and the app works without persistence), so the Diagnostics screen
 * can show whether this install's queue is protected. A THROW is a real failure and is ledgered
 * as one.
 */
export async function requestPersistentStorage(): Promise<void> {
  const storage = typeof navigator === 'undefined' ? undefined : navigator.storage;
  if (storage === undefined || typeof storage.persist !== 'function') {
    ledger.note('STORAGE_PERSIST_UNSUPPORTED', 'boot:storage-persist', {
      userMessage: 'This browser cannot protect offline work from being cleared automatically.',
      notified: false,
    });
    return;
  }

  try {
    // `persisted()` first: already-granted installs must not re-prompt on every boot (some engines
    // treat persist() as a permission request).
    const already = typeof storage.persisted === 'function' ? await storage.persisted() : false;
    const granted = already || (await storage.persist());
    ledger.note(granted ? 'STORAGE_PERSIST_GRANTED' : 'STORAGE_PERSIST_REFUSED', 'boot:storage-persist', {
      userMessage: granted
        ? 'Offline work is protected from automatic clearing.'
        : 'The browser may clear offline work that has not been sent yet. Send while online when you can.',
      notified: false,
      context: { already },
    });
  } catch (error) {
    ledger.record(error, 'boot:storage-persist', { notified: false });
  }
}
